"use client";

import React from "react";
import { ToastContainer } from "react-toastify";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { I18nextProvider } from "react-i18next";
import { Subject } from "rxjs";

import { LivechatProvider, VideoProvider } from "@audacia-hq/shared/contexts";
import { generateFileName } from "@audacia-hq/shared/utils";
import "../services/patch";

import { QueueProvider } from "../context/QueueContext";
import { CurrencyProvider } from "../context/CurrencyContext";
import { ConsultationProvider } from "../context/ConsultationContext";
import { ServiceWorkerProvider } from "../context/ServiceWorkerContext";
import { WSMessage, WSProvider, useWS } from "../context/WSContext";
import { AnnouncementProvider } from "../context/AnnouncementContext";
import { SurveyProvider } from "../context/SurveyContext";
import useTranslation from "../services/i18n/client";
import { stopVideo, sendEventLog } from "../services/api";
import { uploadFile } from "../services/apiClients/storageAPI";
import config from "../services/config";
import { useAuth } from "../context/AuthContext";

import Layout from "./Layout";
import WelcomeOfferModal from "./members/WelcomeOfferModal";

const Application = ({ children }) => {
  const { i18n } = useTranslation("common");
  const { t: livechatTFunc } = useTranslation("livechat");
  const { user } = useAuth();

  return (
    <I18nextProvider i18n={i18n} defaultNS="common">
      <GoogleReCaptchaProvider

        reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_FRONT_API_KEY!}
        useEnterprise
        scriptProps={{
          async: false,
          defer: false,
          appendTo: "head",
          nonce: undefined,
        }}
        container={{
          element: "recaptcha-badge",
          parameters: {
            badge: undefined,
            theme: undefined,
          },
        }}
      >
        <div id="recaptcha-badge" className="hidden" />
        <WelcomeOfferModal />
        <WSProvider>
          <ServiceWorkerProvider>
            <QueueProvider>
              <ToastContainer autoClose={2000} />
              <CurrencyProvider>
                <LivechatProvider
                  userUid={user?.uid}
                  wsBaseHost={config.wsBaseHost}
                  userToken={() => Promise.resolve(user.token)}
                  uploadFile={(file: File, share: string[]) => uploadFile(file, generateFileName(file), "consultations/livechat", share)}
                  t={livechatTFunc}
                >
                  <VideoProvider
                    appId={process.env.NEXT_PUBLIC_VONAGE_APP_ID}
                    closeVideoRoomHandler={(videoRoomUid: string) => stopVideo({
                      variables: { videoRoomUid },
                    })}
                    ws={useWS() as Subject<WSMessage>}
                    t={useTranslation("consultations").t}
                    namespace="consultations"
                    connectivityDisabled={user?.session === "DEEP_LINK"}
                    telemetryHandler={(eventsJsonString) => sendEventLog({
                      variables: {
                        subject: user?.uid,
                        event: "member.video.debug",
                        level: "DEBUG",
                        ts: `${Date.now()}`,
                        metadata: JSON.stringify({
                          events: eventsJsonString,
                        }),
                      },
                    })}
                  >
                    <ConsultationProvider>
                      <AnnouncementProvider>
                        <SurveyProvider>
                          <Layout>{children}</Layout>
                        </SurveyProvider>
                      </AnnouncementProvider>
                    </ConsultationProvider>
                  </VideoProvider>
                </LivechatProvider>
              </CurrencyProvider>
            </QueueProvider>
          </ServiceWorkerProvider>
        </WSProvider>
      </GoogleReCaptchaProvider>
    </I18nextProvider>
  );
};

export default Application;
